import { colors } from '@/theme/colors';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material';
import { useState } from 'react';
import LightLogoImage from '@/assets/images/logo/exp-button-text.svg?react';
import SkyslopeImage from '@/assets/images/logo/skyslope image.svg?react';
import axiosInstance from '@/api/axiosConfig';
import { useAuth } from '@/hooks/useAuth';
import { useChatStore } from '@/hooks/useChatStore';
import { Message } from '@/interface/chat';
import { logRocketUtil } from '@/util/logrocket';
import LogRocket from 'logrocket';

type prop = {
  deepLinkId: string;
  messageObj: Message;
};

const DeepLinkButton: React.FC<prop> = ({ deepLinkId, messageObj }) => {
  const isSkyslope = deepLinkId.includes('skyslope');
  const [isFetching, setIsFetching] = useState(false);
  const { updateMessageRaw } = useChatStore();
  const { authState } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  const skyslopeButtonColor = colors.royal80;
  const skyslopeButtonHoverColor = colors.royal100;
  const skyslopeContentColor = colors.white;
  const color = isSkyslope
    ? skyslopeButtonColor
    : theme.palette.mode === 'dark'
      ? colors.myExpResourcesButton.dark.background
      : colors.myExpResourcesButton.light.background;

  const hoverColor = isSkyslope
    ? skyslopeButtonHoverColor
    : theme.palette.mode === 'dark'
      ? colors.myExpResourcesButton.dark.hover
      : colors.myExpResourcesButton.light.hover;

  const contentColor = isSkyslope
    ? skyslopeContentColor
    : theme.palette.mode === 'dark'
      ? isHovered
        ? colors.white
        : colors.darkGray
      : isHovered
        ? colors.darkGray
        : colors.white;

  const getDeepLink = async () => {
    try {
      if (deepLinkId.includes('https://')) {
        logRocketUtil.trackDeeplinkRedirect({
          deeplinkId: deepLinkId,
          resolvedUrl: deepLinkId,
          isExternal: true,
        });
        window.open(deepLinkId, '_blank');
        return;
      }

      const deepLinkIdNum = parseInt(deepLinkId);
      if (!isNaN(deepLinkIdNum)) {
        setIsFetching(true);
        const response = await axiosInstance.get('/deepLink/getDeepLink', {
          baseURL: process.env.REACT_APP_ORCHESTRATOR_API_URL,
          headers: authState?.idToken?.idToken
            ? {
                Authorization: `Bearer ${authState?.idToken?.idToken}`,
              }
            : undefined,
          params: {
            deeplinkId: deepLinkIdNum,
          },
        });

        const deepLinkUrl = response.data?.data;
        if (deepLinkUrl) {
          logRocketUtil.trackDeeplinkRedirect({
            deeplinkId: deepLinkId,
            resolvedUrl: deepLinkUrl,
            isExternal: false,
          });

          updateMessageRaw({
            ...messageObj,
            deepLink: deepLinkUrl,
          });
          window.open(deepLinkUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Error fetching deep link:', error);
      LogRocket.error('Failed to fetch deep link', error);
    } finally {
      setIsFetching(false);
      setIsHovered(false);
    }
  };
  const deepLinkIdNum = parseInt(deepLinkId);
  if (isNaN(deepLinkIdNum) && !deepLinkId.includes('https://')) {
    return null;
  }
  return (
    <Box
      data-testid="my-exp-resource-button"
      sx={{
        transition: 'transform 150ms ease-in-out',
        '&:hover': {
          transform: !isFetching ? 'scale(1.1)' : '',
          cursor: 'pointer',
        },
        width: '137px',
        height: '47px',
        padding: 0,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Button
        onClick={getDeepLink}
        data-testid="deep-link-button"
        sx={{
          width: '100%',
          height: '100%',
          background: color,
          ':hover': {
            background: hoverColor,
          },
          borderRadius: '8px',
        }}
      >
        <Box
          data-testid="deep-link-content"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '26px',
            width: isSkyslope ? '108px' : '90px',
            color: contentColor,
          }}
        >
          {!isFetching ? (
            isSkyslope ? (
              <SkyslopeImage
                style={{ width: '100%', height: 'auto' }}
                data-testid="skyslope-logo"
              />
            ) : (
              <LightLogoImage
                style={{ width: '100%', height: 'auto' }}
                data-testid="exp-logo"
              />
            )
          ) : (
            <CircularProgress
              sx={{
                p: 1,
                color: contentColor,
              }}
              data-testid="deep-link-loading"
            />
          )}
        </Box>
      </Button>
    </Box>
  );
};
export default DeepLinkButton;
